import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackCTAClick } from "@/utils/analytics";

const fencingPhotos = [
  "https://customer-assets.emergentagent.com/job_fencing-kids-pass/artifacts/4ljvq5sw_40128070_2377551575588273_3749921978903953408_n.jpg",
  "https://customer-assets.emergentagent.com/job_fencing-kids-pass/artifacts/8asf6bdh_27021786_2014567931886641_4564898827470105847_o.jpg",
  "https://customer-assets.emergentagent.com/job_fencing-kids-pass/artifacts/v93swhot_62002015_2985750024768422_5177392937125806080_o.jpg",
];

export default function HeroSection() {
  const scrollToSection = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="min-h-screen flex items-center relative pt-28 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_fencing-kids-pass/artifacts/v93swhot_62002015_2985750024768422_5177392937125806080_o.jpg')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1A2F]/95 via-[#0A1A2F]/90 to-[#0A1A2F]/80"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white space-y-8">
            <div className="inline-flex items-center gap-3 flex-wrap">
              <div className="inline-flex items-center gap-2 bg-[#D4A857]/20 border border-[#D4A857]/40 text-[#D4A857] px-4 py-2 rounded text-sm font-semibold font-['Montserrat'] uppercase tracking-wide">
                Ages 6-16
              </div>
              <div className="inline-flex items-center gap-2 bg-[#3FA9F5]/20 border border-[#3FA9F5]/40 text-[#3FA9F5] px-4 py-2 rounded text-sm font-semibold font-['Montserrat'] tracking-wide" data-testid="hero-ofsted-badge">
                <span>✓</span>
                Ofsted Registered
              </div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white px-4 py-2 rounded text-sm font-semibold font-['Montserrat'] tracking-wide" data-testid="hero-childcare-badge">
                Childcare vouchers &amp; Tax-Free Childcare accepted
              </div>
            </div>

            <p className="text-[#D4A857] font-bold text-base sm:text-lg font-['Montserrat']" data-testid="hero-welcome-line">
              Beginners and experienced fencers welcome
            </p>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight font-['Montserrat']">
              A School-Holiday Adventure:
              <span className="block text-[#D4A857] mt-2">Try Fencing for 5 Days</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/80 max-w-xl leading-relaxed font-['Lato']">
              Join <span className="text-[#D4A857] font-semibold">Masky</span> for a fun, friendly introduction to 
              <span className="text-white font-semibold"> Olympic-style foil fencing</span>. 
              Use your 5 days across <span className="text-white font-semibold">any school holidays within 12 months of purchase</span>.
            </p>

            {/* Flexibility Highlight */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <p className="text-[#D4A857] font-bold mb-4 text-sm uppercase tracking-wide font-['Montserrat']">Mix & Match Your Days:</p>
              <div className="flex flex-wrap gap-3">
                <span className="bg-white/20 px-4 py-2 rounded text-sm font-medium">Oct Half Term</span>
                <span className="bg-white/20 px-4 py-2 rounded text-sm font-medium">Christmas</span>
                <span className="bg-white/20 px-4 py-2 rounded text-sm font-medium">Feb Half Term</span>
                <span className="bg-white/20 px-4 py-2 rounded text-sm font-medium">Easter</span>
                <span className="bg-white/20 px-4 py-2 rounded text-sm font-medium">May Half Term</span>
                <span className="bg-white/20 px-4 py-2 rounded text-sm font-medium">Summer</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => { trackCTAClick("Hero — Get Your 5-Day Pass"); window.open("https://pci.jotform.com/form/260353574059056", "_blank"); }}
                size="lg"
                className="bg-[#D4A857] hover:bg-[#C49A4D] text-[#0A1A2F] font-bold text-base px-8 py-6 rounded shadow-lg hover:shadow-xl transition-all hover:scale-105 font-['Montserrat'] uppercase tracking-wide"
                data-testid="hero-cta-button"
              >
                Get Your 5-Day Pass
              </Button>
              <Button
                onClick={() => scrollToSection("#gallery")}
                variant="outline"
                size="lg"
                className="bg-transparent border-2 border-white/40 text-white hover:bg-white hover:text-[#0A1A2F] font-bold text-base px-8 py-6 rounded transition-all font-['Montserrat'] uppercase tracking-wide"
                data-testid="hero-gallery-button"
              >
                See Our Fencers
              </Button>
            </div>
          </div>

          {/* Right Content - Masky + Fencing Photos */}
          <div className="relative flex flex-col items-center gap-6">
            {/* Masky - Subtle size */}
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_1abea0ec-cded-4678-a29f-de28c813ea65/artifacts/2pfnnew9_bigger%20masky.png"
                alt="Masky welcoming you to Fighting Fit Fencing"
                className="relative w-48 sm:w-56 lg:w-64 masky-glow"
                data-testid="hero-mascot"
              />
            </div>

            {/* Fencing Photos Grid */}
            <div className="grid grid-cols-3 gap-3 w-full max-w-md">
              {fencingPhotos.map((photo, i) => (
                <div 
                  key={i} 
                  className="aspect-square rounded-lg overflow-hidden shadow-lg hover:scale-105 transition-transform border-2 border-[#D4A857]/30"
                >
                  <img
                    src={photo}
                    alt={`Kids fencing at Fighting Fit Fencing ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
            
            {/* Badge */}
            <div className="bg-[#D4A857] text-[#0A1A2F] px-5 py-2 rounded shadow-lg font-bold text-sm font-['Montserrat'] uppercase tracking-wide">
              Real Olympic-Style Fencing • Ages 6-16
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <button
          onClick={() => scrollToSection("#meet-masky")}
          className="text-white/50 hover:text-[#D4A857] transition-colors"
          data-testid="scroll-indicator"
        >
          <ChevronDown size={40} />
        </button>
      </div>
    </section>
  );
}
